import type { OperationalEvent, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, OperationalAction, OperationalSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';
import type { accessRequestsTabScreen } from './site-access-event-data';
import type { UMSource } from './user-management-event-data';
import type { userRequestsScreenNameSource } from './user-requests-event-data';

const smartSettingsRecommendations = 'SmartSettingsRecommendations';
const recommendationTypeDirectAccess = 'DirectAccess';

export const smartSettingsEvents = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/PGT/pages/1301055450/Smart+Settings+Recommendations+4+DA+-+Analytics+spec',
  operational: {
    smartSettingsDismissKeyFetchSucceeded: (attributes: { dismissKey: string }): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: OperationalSubject.SmartSettingsDismissKeyFetch,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/98739',
    }),
    smartSettingsDismissKeyFetchFailed: (attributes: { dismissKey: string; errorMessage: string }): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: OperationalSubject.SmartSettingsDismissKeyFetch,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/98740',
    }),
    smartSettingsDismissKeySavedSucceeded: (attributes: { dismissKey: string }): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: OperationalSubject.SmartSettingsDismissKeySaved,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/98650',
    }),
    smartSettingsDismissKeySavedFailed: (attributes: { dismissKey: string; errorMessage: string }): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: OperationalSubject.SmartSettingsDismissKeySaved,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/98655',
    }),
  },
  screen: {
    smartSettingsModal: (
      attributes: {
        domain: string;
        invitedUsersCount: number;
        suggestedProducts: { productId: string; productAri: string }[];
        triggerSource?: UMSource | typeof accessRequestsTabScreen | typeof userRequestsScreenNameSource.accessRequestsTab;
        daNudgeExpandCohort?: string;
        daExpandAttributionEligible?: boolean;
        daNudgeIterationCohort?: string;
        eligibleForTWCDaNudge?: boolean;
      },
      orgId?: string,
    ): ScreenEvent => ({
      data: {
        name: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/39969',
      orgId,
    }),
  },
  track: {
    approvedDomainSaved: (
      attributes: {
        domain: string;
        invitedUsersCount: number;
        suggestedProducts: { productId: string; productAri: string }[];
        enabledProducts: { productId: string; productAri: string }[];
        triggerSource?: UMSource | typeof accessRequestsTabScreen | typeof userRequestsScreenNameSource.accessRequestsTab;
      },
      orgId?: string,
    ): TrackEvent => ({
      data: {
        action: TrackAction.Saved,
        actionSubject: TrackActionSubject.ApprovedDomain,
        actionSubjectId: attributes.domain,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34089',
      orgId,
    }),
    dismissedSmartSettingsRecommendations: (attributes: {
      suggestedProducts: { productId: string; productAri: string }[];
      suggestedProductsCount: number;
      selectedProducts: { productId: string; productAri: string }[];
      selectedProductsCount: number;
      triggerSource?: UMSource | typeof accessRequestsTabScreen | typeof userRequestsScreenNameSource.accessRequestsTab;
    }): TrackEvent => ({
      data: {
        action: TrackAction.Dismissed,
        actionSubject: TrackActionSubject.SmartSettingsRecommendations,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/96106',
    }),
    smartSettingsModalWrapperChecksCompleted: (
      attributes: {
        domain: string;
        usersCount: number;
        suggestedProducts: { productId: string; productAri: string }[];
        triggerSource?: UMSource | typeof accessRequestsTabScreen | typeof userRequestsScreenNameSource.accessRequestsTab;
        siteId?: string;
        daNudgeIteration1p5InUrmCohort?: string;
        daNudgeIterationCohort?: string;
      },
      orgId?: string,
    ): TrackEvent => ({
      data: {
        action: TrackAction.Completed,
        actionSubject: TrackActionSubject.SmartSettingsModalWrapperChecks,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92172',
      orgId,
    }),
    daNudgeCandidateProductsFetched: (attributes: { productsCount: number; triggerSource?: string }): TrackEvent => ({
      data: {
        action: Action.Fetched,
        actionSubject: TrackActionSubject.DaNudgeCandidateProducts,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/98113',
    }),
  },
  ui: {
    closedSmartSettingsModal: (attributes: {
      suggestedProducts: { productId: string; productAri: string }[];
      suggestedProductsCount: number;
      selectedProducts: { productId: string; productAri: string }[];
      selectedProductsCount: number;
      triggerSource?: UMSource | typeof accessRequestsTabScreen | typeof userRequestsScreenNameSource.accessRequestsTab;
    }): UIEvent => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: smartSettingsRecommendations,
        source: smartSettingsRecommendations,
        attributes: {
          ...attributes,
          recommendationType: recommendationTypeDirectAccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/96109',
    }),
  },
};
